import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import './ControlledTreeview.css';
import { splitToChunks } from '../../../utils/arrays';
import Checkmark from '../../../../public/assets/checkmark.svg';
import colors from '../../../constants/colors';

const useViewStyles = makeStyles({
    root: {
    }
});

const useItemStyles = makeStyles(() => ({
    content: {
        flexDirection: 'row-reverse',
        marginLeft: '8px'
    },
    root: {
        '& > .MuiTreeItem-content > .MuiTreeItem-label': {
            display: 'flex',
            alignItems: 'center',
            padding: '4px 0',
            background: 'transparent !important',
            pointerEvents: 'none',
            fontSize: 'var(--subheader)'
        },
        '& > .MuiTreeItem-content  > .MuiTreeItem-label::before': {
            content: '""',
            display: 'inline-block',
            width: 12,
            height: 12,
            marginRight: 8,
            border: '1px solid #ccc',
            background: '#919191'
        }
    },
    iconContainer: {
        marginRight: 12,
        '& > svg': {
            padding: 8,
            '&:hover': {
                opacity: 0.6
            }
        }
    },
    label: {
        padding: 0
    },
    selected: {
        '& > .MuiTreeItem-content  > .MuiTreeItem-label::before': {
            background: 'var(--primary)',
            border: '1px solid transparent',
            backgroundImage:`url(${Checkmark})`,
            backgroundPosition: 'center', 
            backgroundSize: 'contain', 
        }
    }
}));
const useStyles = makeStyles(() => ({
    circular: {
        color: colors.blue
    }
}));

export default function ControlledTreeView({ handleSelection, selectedItems, item, allExpanded, isLoading, childless }) {
    const classesView = useViewStyles();
    const classesItem = useItemStyles();
    const classes = useStyles();

    const [numberOfBatch, setNumberOfBatch] = useState(0);
    const [parentClientBatches, setParentClientBatches] = useState([]);
    const [expanded, setExpanded] = useState([]);

    const handleToggle = (event, nodeIds) => {
        if (event.target.nodeName !== 'svg') {
            return;
        }

        setExpanded(nodeIds);
    };

    const handleClickOnMore = () => {
        if (numberOfBatch < parentClientBatches.length) {
            setNumberOfBatch(numberOfBatch + 1);
            const selection = document.querySelector('.box-controlled-treeview > ul > li:nth-child(1)');
            selection.scrollIntoView({ behavior: 'smooth' });
        }
    }

    const handleClickOnPrevious = () => {
        if (numberOfBatch > 0) {
            setNumberOfBatch(numberOfBatch - 1);
        }
    }

    useEffect(() => {
        if (item) {
            if (item.length > 200) {
                const batched = splitToChunks(item, 10);
                setParentClientBatches(batched);
            }
            else if (item.length > 0) {
                const batched = splitToChunks(item, 1);
                setParentClientBatches(batched);
            }
            else {
                setParentClientBatches([]);
            }
        }
    }, [item]);

    useEffect(() => {
        if (allExpanded) {
            if (parentClientBatches.length > 0) {
                const expandedParents = parentClientBatches[numberOfBatch]
                    .map((parentClient) => parentClient.ParentId)
                    .filter(id => id != null); // Filter out undefined or null IDs
                setExpanded(expandedParents);
            }
        }
    }, [numberOfBatch, parentClientBatches, allExpanded]);

    const treeviewContent = () => {
        if (parentClientBatches.length < 1 && isLoading) {
            return (
                <Box className='box-loading'>
                    <CircularProgress 
                        size={50}
                        thickness={4}
                        value={100}
                        className={classes.circular} 
                    />
                </Box>
            );
        }
        
        if (!parentClientBatches.length) {
            return <Box className='not-found'>Clients not found</Box>;
        }
        
        return parentClientBatches[numberOfBatch].map((clientItem) => {
            const parentNodeId = clientItem.ParentId || `parent-${Math.random().toString(36).substr(2, 9)}`;
            const children = [];

            if (clientItem.children && Array.isArray(clientItem.children)) {
                clientItem.children.forEach((clientChild) => {
                    const childNodeId = clientChild.ChildrenId || `child-${Math.random().toString(36).substr(2, 9)}`;
                    children.push(
                        <TreeItem 
                            key={childNodeId} 
                            classes={!childless ? classesItem : {}} 
                            nodeId={!childless ? childNodeId : childNodeId} 
                            label={clientChild.ChildrenLabel || "Unnamed Child"} 
                        />
                    );
                });
            }

            if (children.length) {
                return (
                    <TreeItem 
                        key={parentNodeId} 
                        classes={classesItem} 
                        nodeId={parentNodeId} 
                        label={clientItem.ParentLabel || "Unnamed Parent"}
                    >
                        {children}
                    </TreeItem>
                );
            }

            return (
                <TreeItem 
                    key={parentNodeId} 
                    classes={classesItem} 
                    nodeId={parentNodeId} 
                    label={clientItem.ParentLabel || "Unnamed Parent"} 
                />
            );
        });
    }

    return (
        <Box className='box-controlled-treeview'>
            {numberOfBatch > 0 && parentClientBatches.length > 0 && <Button id='previous-button' onClick={handleClickOnPrevious}>See Previous</Button>}
            <TreeView
                disableSelection={isLoading}
                classes={classesView}
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
                expanded={expanded}
                selected={selectedItems}
                onNodeToggle={handleToggle}
                onNodeSelect={handleSelection}
                multiSelect
            >
                {treeviewContent()}
            </TreeView>
            {parentClientBatches.length > 1 && <Button onClick={handleClickOnMore}>Load More</Button>}
        </Box>
    );
}

/* eslint-disable react/forbid-prop-types */
ControlledTreeView.propTypes = {
    handleSelection: PropTypes.func.isRequired,
    selectedItems: PropTypes.array.isRequired,
    item: PropTypes.array,
    allExpanded: PropTypes.bool,
    isLoading: PropTypes.bool.isRequired,
    childless: PropTypes.bool
}

ControlledTreeView.defaultProps = {
    item: [],
    allExpanded: false,
    childless: false
}
